import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Send, Users, Store, Truck, Shield, Bell, Loader2, 
  CheckCircle, Wifi, Mail, Smartphone, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const AUDIENCES = [
  { id: 'all', label: 'All Users', icon: Users, description: 'Send to everyone' },
  { id: 'customers', label: 'Customers', icon: Users, description: 'App customers only' },
  { id: 'restaurants', label: 'Restaurants', icon: Store, description: 'Restaurant admins' },
  { id: 'drivers', label: 'Drivers', icon: Truck, description: 'Delivery drivers' },
];

export default function BroadcastNotifications() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({
    total_notifications: 0,
    unread_notifications: 0,
    users_with_push_tokens: 0,
    users_with_digest_enabled: 0,
    online_users: 0,
    notifications_by_type: {},
  });

  const [broadcast, setBroadcast] = useState({
    audience: 'all',
    title: '',
    message: '',
    action_url: '',
    send_push: true,
    send_email: false,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/notification-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcast.title || !broadcast.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      setSending(true);
      const response = await api.post('/admin/send-notification', broadcast);
      toast.success(`Notification sent to ${response.data.in_app_count} users`);
      setBroadcast(prev => ({ ...prev, title: '', message: '', action_url: '' }));
      fetchStats();
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Broadcast Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">
          Send notifications to all users or specific groups
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_notifications}</p>
                <p className="text-xs text-slate-500">Total Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.unread_notifications}</p>
                <p className="text-xs text-slate-500">Unread</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.users_with_push_tokens}</p>
                <p className="text-xs text-slate-500">Push Enabled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.users_with_digest_enabled}</p>
                <p className="text-xs text-slate-500">Digest Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Wifi className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.online_users}</p>
                <p className="text-xs text-slate-500">Online Now</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Broadcast Form */}
        <Card>
          <CardHeader>
            <CardTitle>Send Broadcast</CardTitle>
            <CardDescription>Send notification to a group of users</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audience Selection */}
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <div className="grid grid-cols-2 gap-2">
                {AUDIENCES.map((aud) => (
                  <button
                    key={aud.id}
                    onClick={() => setBroadcast(prev => ({ ...prev, audience: aud.id }))}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      broadcast.audience === aud.id 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <aud.icon className={`w-4 h-4 ${
                        broadcast.audience === aud.id ? 'text-orange-600' : 'text-slate-500'
                      }`} />
                      <span className={`font-medium ${
                        broadcast.audience === aud.id ? 'text-orange-900' : 'text-slate-700'
                      }`}>
                        {aud.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{aud.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Notification title..."
                value={broadcast.title}
                onChange={(e) => setBroadcast(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Notification message..."
                value={broadcast.message}
                onChange={(e) => setBroadcast(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Action URL (optional)</Label>
              <Input
                placeholder="/orders or https://..."
                value={broadcast.action_url}
                onChange={(e) => setBroadcast(prev => ({ ...prev, action_url: e.target.value }))}
              />
              <p className="text-xs text-slate-500">Where to navigate when notification is clicked</p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-slate-500" />
                  <Label>Send Push Notification</Label>
                </div>
                <Switch
                  checked={broadcast.send_push}
                  onCheckedChange={(checked) => setBroadcast(prev => ({ ...prev, send_push: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <Label>Send Email</Label>
                </div>
                <Switch
                  checked={broadcast.send_email}
                  onCheckedChange={(checked) => setBroadcast(prev => ({ ...prev, send_email: checked }))}
                />
              </div>
            </div>

            <Button
              onClick={sendBroadcast}
              disabled={sending || !broadcast.title || !broadcast.message}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Broadcast
            </Button>
          </CardContent>
        </Card>

        {/* Notification Types Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Types</CardTitle>
            <CardDescription>Breakdown by notification type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.notifications_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <Bell className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">{count}</span>
                </div>
              ))}
              {Object.keys(stats.notifications_by_type || {}).length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No notifications sent yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
